<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {AxiosError} from "axios";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import StatusDot from "@/core/components/ui/badge/StatusDot.vue";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Label} from "@/core/components/ui/label";
import {CircleCheck, CircleX, CreditCard, Eye, EyeOff, LoaderCircle} from "@lucide/vue";
import useCpsLocator from "@/amelipro/composables/useCpsLocator";
import useAmelipro from "@/amelipro/composables/useAmelipro";
import useCpsCode from "@/amelipro/composables/useCpsCode";
import {canSignIn, useCpsStore} from "@/stores/cps.store";
import {supportsAdac} from "@/core/services/shellCapabilities";
import {CpsCardDetection} from "@/core/interfaces/CpsLocator";
import {AmeliproCpsAuthentication, AmeliproCpsCertificate} from "@/amelipro/interfaces";
import dateHelper from "@/core/helpers/dateHelper";
import Extension from "@/core/interfaces/extension/Extension";
import {Contract} from "@/core/interfaces/Contract";
import {contractApi} from "@/container";
import imagesHelper from "@/core/helpers/imagesHelper";
import AdacConsentText from "@/extension/components/extension-configurators/adac/AdacConsentText.vue";

const props = defineProps<{
    /** The ADAC module, as the pharmacy has it. */
    extension: Extension;
    /** Record that the pharmacy went through the configuration, and say whether it was. */
    configure: () => Promise<boolean>;
}>();

const locator = useCpsLocator();
const amelipro = useAmelipro();
const vault = useCpsCode();
const cpsStore = useCpsStore();

/**
 * Whether the pharmacy's CPS map is shown under the first screen.
 *
 * Hidden for now: the screen says what to do without it. The map itself keeps
 * being kept up to date, so putting it back is this one flag.
 */
/**
 * The pharmacy's posts are listed when this one cannot carry the module and
 * no gateway does it already: that list is where the pharmacist is sent.
 */
const showPharmacyPosts = computed(() => !realCpsHere.value && !remoteGateway.value);

/** Whether this Connect is able to carry the module at all. */
const ready = supportsAdac();

/**
 * The workstation, the terms of the feature, the card and its code, then —
 * once all are done — the configured module. A module the pharmacy already
 * configured opens there: replaying the card to reach it would prove nothing new.
 */
const step = ref<1 | 2 | 3 | 4>(props.extension.configured ? 4 : 1);

/** What each step is about, as the header names it. */
const STEP_NAMES: Record<number, string> = {1: 'Poste de travail', 2: 'Conditions d\'utilisation', 3: 'Carte CPS'};

/** The contract carrying the terms of the feature, by its slug. */
const CONSENT_CONTRACT = 'adac';

const checking = ref(false);
const code = ref('');
const revealed = ref(false);
const verifying = ref(false);
const verified = ref(false);
const verification = ref<string | null>(null);

const saveError = ref<string | null>(null);
/** Whether the configuration is being recorded. */
const saving = ref(false);
/** Whether this workstation already holds a code porteur for this card. */
const codeKept = ref(false);
const certificate = ref<AmeliproCpsCertificate | null>(null);

/** Whether this shell has a credential store to file the code in. */
const vaultAvailable = computed(() => vault.isAvailable());

/** The code may be filed once it is proven and has somewhere to go. */
const canSave = computed(() => verified.value && vaultAvailable.value);

/** A code is at hand: either typed here, or already kept by this workstation. */
const hasCode = computed(() => !!code.value.trim() || codeKept.value);

/** A code at hand, and no sign-in under way. */
const canVerify = computed(() => hasCode.value && !verifying.value);

/** The workstation this Connect runs on, as the pharmacy map names it. */
const hostname = computed(() => cpsStore.hostname);

/** Host names run long — a machine name is an identifier, not a sentence. */
const HOSTNAME_MAX_LENGTH = 25;

/** The host name as it is shown, cut when it would push the sentence around. */
const shortHostname = computed(() => {
    const name = hostname.value;

    return name && name.length > HOSTNAME_MAX_LENGTH
        ? `${name.slice(0, HOSTNAME_MAX_LENGTH)}…`
        : name;
});

/** Whether a CPS card answered on this workstation. */
const cpsHere = computed(() => cpsStore.hasLocalCard);

/**
 * Whether the card here can open an amelipro session.
 *
 * The service cards — CPA, CPE, CPF, CDE — are turned away by the portal: a
 * post that holds one is not a post to configure the module on.
 */
const realCpsHere = computed(() => cpsHere.value && canSignIn(card.value?.family));

/** A card is here, but not one the portal lets in. */
const serviceCardHere = computed(() => cpsHere.value && !realCpsHere.value);

/** The gateway another post already is, when the pharmacy has one. */
const remoteGateway = computed(() => cpsStore.remoteGateway);

/**
 * What the reader of this workstation last said about its card.
 *
 * Kept here whole rather than read back from the pharmacy map: the map carries
 * what the other posts need to know, this screen shows what the card is.
 */
const detection = ref<CpsCardDetection | null>(null);

/** Everything known about the card in this reader, whoever found it. */
const card = computed(() => detection.value ?? cpsStore.localPost ?? null);

/**
 * The holder of the card inserted here.
 *
 * It travels with the detection now: the middleware reads it off the card's
 * certificate, which no card family withholds.
 */
const holder = computed(() => card.value?.holder ?? null);

/** What the certificate calls the holder, and the entity it names. */
const identity = computed(() => card.value
    ? {title: card.value.title, entity: card.value.entity}
    : null);

/** The card family the reader names: CPS, CPE, CPF, CDE — or CPA when it refuses it. */
const cardFamily = computed(() => card.value?.family ?? null);

/** The day the inserted card stops being valid, as the card itself gives it. */
const validUntil = computed(() => {
    const day = card.value?.validUntil;

    return day ? dateHelper.shortFormat(day, 'DD MMMM YYYY') : null;
});

/**
 * Keep a detection, and hand it to the pharmacy map whole.
 *
 * One place builds the report, so a field added to the card never goes missing
 * on one of the two paths that lead here.
 *
 * @param found What the reader answered.
 */
const keepDetection = (found: CpsCardDetection): void => {
    detection.value = found;

    if (!cpsStore.hostname) {
        return;
    }

    cpsStore.report({
        hostname: cpsStore.hostname,
        present: found.present,
        holder: found.holder,
        civility: found.civility,
        family: found.family,
        title: found.title,
        entity: found.entity,
        validUntil: found.validUntil
    });
};

/**
 * Leave the workstation step: for the terms, or straight for the card when the
 * pharmacy already accepted their current version — the holder accepts them
 * once, for every post of the pharmacy. Unread, they are shown: their step
 * says what went wrong.
 */
const leaveWorkstation = async (): Promise<void> => {
    const accepted = await contractApi().readAccepted().catch(() => []);

    step.value = accepted.some(({slug}) => slug === CONSENT_CONTRACT) ? 3 : 2;
};

/**
 * Move on to the card step, once the CPS is on this post.
 *
 * The map answers on its own as a rule; the direct probe is what catches a
 * card inserted a second ago, and covers a pharmacy channel out of reach.
 */
const start = async (): Promise<void> => {
    if (realCpsHere.value) {
        await leaveWorkstation();

        return;
    }

    // The card may have been slid in since the screen opened: this button is
    // how the pharmacist says so, and it is the only way to ask again.
    checking.value = true;

    try {
        const found = await locator.probe({fresh: true});

        keepDetection(found);

        if (found.present && canSignIn(found.family)) {
            await leaveWorkstation();
        }
    } finally {
        checking.value = false;
    }
};

/** The contract of the terms, with the version the pharmacist accepts. */
const consent = ref<Contract | null>(null);

/** Whether the pharmacist ticked the acceptance of the terms. */
const consentChecked = ref(false);

/** Whether the acceptance is being recorded. */
const consentSaving = ref(false);

/** Why the terms could not be read, or the acceptance recorded. */
const consentError = ref<string | null>(null);

/**
 * Whether the terms were asked of a module already configured: once they are
 * accepted, the pharmacy goes back to its configuration without replaying the card.
 */
let resumeConfigured = false;

/** The terms are accepted once ticked, and only once there is a version to accept. */
const canAcceptConsent = computed(() => consentChecked.value && !!consent.value?.version && !consentSaving.value);

/**
 * Read the terms' contract, and whether the pharmacy already accepted its
 * current version: the box is then ticked already, and the step is a
 * reminder rather than a question.
 */
const loadConsent = async (): Promise<void> => {
    consentError.value = null;

    try {
        const [contract, accepted] = await Promise.all([
            contractApi().read(CONSENT_CONTRACT),
            contractApi().readAccepted(),
        ]);

        consent.value = contract;
        consentChecked.value = accepted.some(({slug}) => slug === CONSENT_CONTRACT);
    } catch {
        consent.value = null;
        consentError.value = 'Les conditions d\'utilisation d\'ADAC n\'ont pas pu être chargées. '
            + 'Vérifiez la connexion, puis réessayez.';
    }
};

// The terms are read as their step opens: the configuration cannot go past
// them without the version the pharmacist is accepting.
watch(step, (current) => {
    if (current === 2) {
        loadConsent();
    }
});

/**
 * Record the acceptance of the terms, then move on to the card.
 *
 * The API files it under the account signed in, with the time and the
 * version accepted: that is the proof of consent. Nothing goes further
 * until it is recorded.
 */
const acceptConsent = async (): Promise<void> => {
    if (!canAcceptConsent.value) {
        return;
    }

    consentSaving.value = true;
    consentError.value = null;

    try {
        await contractApi().accept(consent.value!.version!.id);
        step.value = resumeConfigured ? 4 : 3;
        resumeConfigured = false;
    } catch (error) {
        consentError.value = (error as AxiosError)?.response?.status === 403
            ? 'Seul le compte titulaire de la pharmacie peut accepter ces conditions.'
            : 'Votre acceptation n\'a pas pu être enregistrée. Vérifiez la connexion, puis réessayez.';
    } finally {
        consentSaving.value = false;
    }
};

/** The PDF of the version being accepted, for the pharmacy's own records. */
const openConsentPdf = (): void => {
    const version = consent.value?.version;

    if (version) {
        window.open(imagesHelper.getURLFromPath(
            `contracts/versions/${version.id}/show?document=${encodeURIComponent(version.file)}`
        ));
    }
};

/**
 * What to tell the pharmacist at the end of a sign-in cycle.
 *
 * @param outcome The outcome the shell reports.
 */
const verificationMessage = (outcome: AmeliproCpsAuthentication): string => {
    if (outcome.authenticated) {
        return 'Code vérifié';
    }

    // Refused with nothing played: the card or Windows answered, and the
    // pharmacist hears it at once rather than through a prompt of the system's.
    if (outcome.step === 'code') {
        switch (outcome.reason) {
            case 'wrong-code':
                return 'Code refusé : vérifiez-le avant de réessayer (trois erreurs bloquent la carte)';
            case 'locked':
                return 'Carte bloquée : faites-la débloquer avant de continuer';
            case 'final-try':
                return 'Un seul essai restant : déverrouillez la carte avec le Gestionnaire de carte CPS';
            case 'no-token':
                return 'Aucune carte dans le lecteur';
            case 'no-certificate':
                return 'Windows ne connaît pas le certificat de cette carte : retirez-la, réinsérez-la, puis réessayez';
            default:
                return 'Aucun code enregistré sur ce poste';
        }
    }

    if (outcome.step === 'manual') {
        return 'Connectez-vous dans la fenêtre AméliePro qui vient de s\'ouvrir, puis réessayez';
    }

    return 'Code non vérifié : réessayez';
};

/**
 * Launch the ghost sign-in, which is what proves the code is the right one.
 *
 * Connect clicks the card entry of the portal, then the middleware takes the
 * hand: it reads the reader, asks which certificate to use and prompts for the
 * code porteur in a window of its own. The session opening at the end of that
 * cycle is the proof we wait for before letting the code be saved.
 */
const verify = async (): Promise<void> => {
    if (!canVerify.value) {
        return;
    }

    verifying.value = true;
    verification.value = null;
    saveError.value = null;

    try {
        // The code is filed before the cycle runs, not after: the shell reads
        // it from the vault to unlock the card, so the very first sign-in can
        // already do without the system's prompt.
        if (code.value.trim() && !await vault.keep(code.value)) {
            saveError.value = 'Votre code n\'a pas pu être rangé dans le coffre-fort de Windows. '
                + 'Réessayez, et prévenez-nous si cela se reproduit.';

            return;
        }

        codeKept.value = true;

        // The pharmacist is right here: when Connect cannot click the card
        // entry for them, the portal is handed over for them to sign in —
        // which is what a sign-in asked for without `background` does.
        const outcome = await amelipro.authenticateWithCps();

        verified.value = outcome.authenticated;
        verification.value = verificationMessage(outcome);

        // A sign-in that went through is what makes this post a gateway, and
        // the shell has just written that down. The notification window —
        // which owns the reader and the pharmacy map — is told to look again
        // now, from the reader and not from its cache.
        if (outcome.authenticated) {
            locator.requestGateways();
        }

        // A refused code has been forgotten by the shell: the screen says so,
        // rather than showing a code as kept that no longer is.
        if (outcome.step === 'code' && outcome.reason === 'wrong-code') {
            codeKept.value = false;
        }

        // The card's own certificate, which the middleware played: the only
        // place its registering entity and its validity ever show up.
        certificate.value = outcome.certificate ?? null;
    } finally {
        verifying.value = false;
    }
};

// A code that changes is a code that is not proven anymore.
watch(code, () => {
    verified.value = false;
    verification.value = null;
});

/**
 * Pick up the code this workstation already keeps for the card in its reader.
 *
 * The code itself never comes back to this screen — the vault has no way of
 * handing it over, by design. What comes back is that there is one, which is
 * enough: the field says a code is kept.
 */
onMounted(async () => {
    codeKept.value = await vault.isKept();
});

/**
 * A module configured without the current version of its terms — before they
 * existed, or since they changed — opens on them: no pharmacy uses ADAC on
 * terms it has not accepted. When they cannot be read, the configuration
 * stands, and they are asked at the next opening.
 */
onMounted(async () => {
    if (step.value !== 4) {
        return;
    }

    try {
        const accepted = await contractApi().readAccepted();

        if (!accepted.some(({slug}) => slug === CONSENT_CONTRACT)) {
            resumeConfigured = true;
            step.value = 2;
        }
    } catch {
        // Left on the configured module.
    }
});

/**
 * Finish the configuration.
 *
 * The code was filed when it was proven, so there is nothing left to write
 * here: this only says that the pharmacy has been through the screen.
 */
const onSave = async (): Promise<void> => {
    if (!canSave.value || saving.value) {
        return;
    }

    revealed.value = false;
    saving.value = true;

    try {
        if (await props.configure()) {
            step.value = 4;
        }
    } finally {
        saving.value = false;
    }

    // The notification window of this very Connect owns the reader. A scan
    // refreshes the pharmacy map; the gateway request makes that window
    // re-probe the reader rather than answer from its cache, so this post
    // is announced as the gateway it now is — that is what shows the ADAC
    // entry in the popup — and every other post learns where its calls go.
    locator.refresh();
    locator.requestGateways();
};

/**
 * Read the card in this very post's reader, afresh.
 *
 * What decides this screen is the card on this very post, so it is read here
 * rather than waited for on the pharmacy channel.
 */
const detectCard = async (): Promise<void> => {
    checking.value = true;

    try {
        const found = await locator.probe({fresh: true});

        if (found.conclusive) {
            keepDetection(found);
        }
    } finally {
        checking.value = false;
    }
};

/**
 * Start the configuration over, from the workstation.
 *
 * The code stays in the vault until another one is proven: what starts over
 * is the proof — the sign-in is played again before the configuration can be
 * finished anew — along with the card, read afresh.
 */
const restart = async (): Promise<void> => {
    // What was typed stays out of the field: the code that was proven is in
    // the vault, and the field says so.
    code.value = '';
    revealed.value = false;
    verified.value = false;
    verification.value = null;
    certificate.value = null;
    saveError.value = null;
    resumeConfigured = false;
    step.value = 1;

    await detectCard();
};

onMounted(async () => {
    if (!cpsStore.hostname) {
        cpsStore.identify(await window.ipcRenderer?.invoke('getHostname') as string ?? null);
    }

    // The gateway answers from its reader, not from a report it cached
    // before it became one: the last step names it.
    locator.refresh();
    locator.requestGateways();

    await detectCard();
});
</script>

<template>
  <BaseCard>
    <BaseCardBody>
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-bold text-sm">
          {{ step === 4 ? 'Module ADAC configuré ✅' : 'Paramétrage' }}
        </h3>
        <span class="text-gray-500 text-xs">
          {{ step === 4 ? 'Configuration terminée' : `Étape ${step} sur 3 · ${STEP_NAMES[step]}` }}
        </span>
      </div>

      <!-- A Connect that cannot yet carry the module says so and stops
           there: every screen below assumes a card it has no way of reading,
           and would send the pharmacist hunting for a problem of their own. -->
      <div v-if="!ready" class="flex items-start gap-3 rounded-lg bg-lightPurple-lightest p-4">
        <CreditCard class="text-primary shrink-0" :size="24" />
        <div class="flex-1 text-sm">
          <p class="font-bold">
            Service bientôt disponible
          </p>
          <p class="text-gray-600">
            Ce poste attend une mise à jour d'Apodis Connect pour prendre en
            charge votre carte CPS. Elle s'installera toute seule dans les
            prochains jours, et le paramétrage deviendra alors accessible.
          </p>
        </div>
      </div>

      <template v-else-if="step === 1">
        <div class="flex items-center gap-3 rounded-lg bg-lightPurple-lightest p-4">
          <LoaderCircle v-if="checking" class="animate-spin text-primary shrink-0" :size="24" />
          <CreditCard v-else class="text-primary shrink-0" :size="24" />
          <div class="flex-1 text-sm">
            <template v-if="checking">
              <p class="font-bold">
                Nous détectons votre carte CPS
              </p>
            </template>
            <template v-else-if="realCpsHere">
              <p class="font-bold">
                {{ cardFamily ? `Carte ${cardFamily} détectée` : 'Carte CPS détectée' }}
              </p>
              <p class="text-gray-600">
                Vous êtes sur le poste
                <span class="font-bold" :title="hostname ?? undefined">{{ shortHostname }}</span>.
                Vous pouvez continuer la configuration du module.
              </p>
            </template>
            <template v-else-if="serviceCardHere">
              <p class="font-bold">
                Rendez-vous sur un poste équipé de la CPS
              </p>
              <p class="text-gray-600">
                Vous êtes sur le poste
                <span class="font-bold" :title="hostname ?? undefined">{{ shortHostname }}</span>.
                Carte {{ cardFamily }} détectée : cette carte ne permet pas de se connecter à
                AméliePro. Ouvrez le module ADAC depuis un poste avec CPS.
              </p>
            </template>
            <template v-else>
              <p class="font-bold">
                Rendez-vous sur un poste équipé de la CPS
              </p>
              <p class="text-gray-600">
                Vous êtes sur le poste
                <span class="font-bold" :title="hostname ?? undefined">{{ shortHostname }}</span>.
                Pas de CPS détectée. Ouvrez le module ADAC depuis un poste avec CPS.
              </p>
            </template>
          </div>
          <!-- The margin sits on the action, not on the row's gap: the icon
               stays tight against the text it belongs to. -->
          <div class="flex items-center shrink-0 ml-8">
            <BaseButton primary :disabled="checking" @click="start">
              {{ realCpsHere ? 'Continuer la configuration' : 'J\'y suis, commencer la configuration' }}
            </BaseButton>
          </div>
        </div>

        <!-- The pharmacy already has its gateway: this post has nothing to set
             up, and says so — with the name of the post that does the work. -->
        <div v-if="remoteGateway" class="mt-4 flex items-start gap-3 rounded-lg bg-lightPurple-lightest p-4">
          <StatusDot state="up_to_date" class="mt-1" />
          <div class="text-sm">
            <p class="font-bold">
              Configuration terminée
            </p>
            <p class="text-gray-600">
              Le poste <span class="font-bold">{{ remoteGateway.hostname }}</span> est le poste passerelle
              de la pharmacie<template v-if="remoteGateway.holder"> — carte de {{ remoteGateway.holder }}</template>.
              Les vérifications ADAC de ce poste passent par lui.
            </p>
          </div>
        </div>

        <div v-if="cpsHere" class="mt-4 flex items-start gap-3 rounded-lg bg-lightPurple-lightest p-4">
          <StatusDot state="up_to_date" class="mt-1" />
          <div class="text-sm">
            <p class="font-bold">
              {{ holder || 'Titulaire inconnu' }}
            </p>
            <p v-if="identity?.title || identity?.entity" class="text-gray-500">
              {{ [identity?.title, identity?.entity].filter(Boolean).join(' · ') }}
            </p>
            <p v-if="validUntil" class="text-gray-500">
              Valable jusqu'au {{ validUntil }}
            </p>
          </div>
        </div>

        <div v-if="showPharmacyPosts" class="mt-6">
          <div class="flex items-center justify-between mb-2">
            <h4 class="font-bold text-sm">
              Postes de la pharmacie
            </h4>
            <button
              class="text-primary text-xs font-medium disabled:opacity-50"
              :disabled="cpsStore.scanning"
              @click="locator.refresh()"
            >
              {{ cpsStore.scanning ? 'Analyse en cours…' : 'Relancer l\'analyse' }}
            </button>
          </div>

          <template v-if="cpsStore.signInPosts.length">
            <p
              v-for="post in cpsStore.signInPosts"
              :key="post.hostname"
              class="flex items-center text-sm py-1"
            >
              <StatusDot state="up_to_date" class="mr-2" />
              Carte CPS sur {{ post.hostname }}<template v-if="post.holder"> : {{ post.holder }}</template>
              <span v-if="post.hostname === hostname" class="text-gray-500 ml-1">(ce poste)</span>
            </p>
          </template>
          <p v-else-if="cpsStore.scanning" class="text-gray-500 text-sm">
            Analyse des postes en cours…
          </p>
          <p v-else class="text-gray-500 text-sm">
            Aucune carte CPS détectée sur les postes de la pharmacie.
          </p>
        </div>
      </template>

      <!-- The terms, in full, before anything is asked of the card: the
           configuration goes no further until their acceptance is recorded. -->
      <template v-else-if="step === 2">
        <div class="max-h-[45vh] overflow-y-auto rounded-lg border p-4">
          <AdacConsentText />
        </div>

        <div class="mt-4 flex items-start gap-2 text-sm">
          <Checkbox id="adac-consent" v-model="consentChecked" class="mt-0.5" />
          <Label for="adac-consent" class="leading-snug">
            J’ai lu et j’accepte les conditions d’utilisation de la fonctionnalité ADAC dans Apodis et
            j’autorise son activation.
          </Label>
        </div>

        <p v-if="consentError" class="mt-3 text-sm text-red-600 font-medium">
          {{ consentError }}
        </p>

        <div class="mt-4 flex items-center justify-between gap-4">
          <button
            v-if="consent?.version"
            type="button"
            class="text-primary text-xs font-medium"
            @click="openConsentPdf"
          >
            Télécharger les conditions (PDF)
          </button>
          <span v-else />
          <BaseButton primary :disabled="!canAcceptConsent" @click="acceptConsent">
            {{ consentSaving ? 'Enregistrement…' : 'Suivant' }}
          </BaseButton>
        </div>
      </template>

      <template v-else-if="step === 3">
        <h2 class="text-primary font-bold mb-2">
          Bienvenue sur le programme ADAC
        </h2>
        <p class="text-gray-600 text-sm mb-4">
          L'appel se fait avec votre carte CPS : vérifiez que vous êtes sur le
          poste où elle est insérée.
        </p>

        <p class="mb-4 flex items-center text-gray-500 text-sm">
          <StatusDot state="up_to_date" class="mr-2" />
          {{ cardFamily ? `Carte ${cardFamily}` : 'Carte CPS' }}<template v-if="holder"> · {{ holder }}</template><template v-if="identity?.entity"> · {{ identity.entity }}</template>
        </p>

        <div class="text-sm">
          <div class="max-w-md">
            <div>
              <label class="block font-medium mb-1" for="adac-code-porteur">
                Code porteur
              </label>
              <div class="flex items-center gap-2">
                <div class="relative flex-1 max-w-xs">
                  <input
                    id="adac-code-porteur"
                    v-model="code"
                    :type="revealed ? 'text' : 'password'"
                    inputmode="numeric"
                    maxlength="4"
                    autocomplete="off"
                    :placeholder="codeKept ? 'Code enregistré' : ''"
                    class="rounded-lg border px-2 py-1 pr-9 w-full"
                    @keyup.enter="verify"
                  >
                  <button
                    type="button"
                    class="absolute inset-y-0 right-2 flex items-center text-gray-500"
                    :aria-label="revealed ? 'Masquer le code' : 'Afficher le code'"
                    @click="revealed = !revealed"
                  >
                    <component :is="revealed ? EyeOff : Eye" :size="16" />
                  </button>
                </div>
                <BaseButton primary :disabled="!canVerify" @click="verify">
                  {{ verifying ? 'Vérification…' : 'OK' }}
                </BaseButton>
              </div>

              <p v-if="verifying" class="mt-2 text-gray-500">
                Vérification en cours, cela prend quelques secondes.
              </p>

              <!-- One line: what the pharmacist does next. The steps of the
                   sign-in are in main.log, `[amelipro]` lines, for support. -->
              <p
                v-if="verification"
                class="mt-2 flex items-center gap-1.5"
                :class="verified ? 'text-gray-600' : 'text-red-600 font-medium'"
              >
                <CircleCheck v-if="verified" :size="16" class="shrink-0 text-green" />
                <CircleX v-else :size="16" class="shrink-0" />
                <span>{{ verification }}</span>
              </p>

              <p v-if="!vaultAvailable" class="text-gray-500 mt-2">
                Cette version de Connect ne sait pas encore ranger votre code en
                sécurité. Elle se mettra à jour toute seule dans les prochains jours.
              </p>

              <p v-if="saveError" class="mt-3 text-red-600 font-medium">
                {{ saveError }}
              </p>

            </div>
          </div>

          <div class="mt-4 flex justify-end">
            <!-- The name never changes: it says what the button is for, and
                 being greyed out already says it is not available yet. -->
            <BaseButton primary :disabled="!canSave || saving" @click="onSave">
              Terminer la configuration
            </BaseButton>
          </div>
        </div>
      </template>

      <!-- Reached from a configuration the API recorded: the one just made, or
           the pharmacy's own when the module opens configured. Its targetings
           are the program page's. -->
      <template v-else>
        <div class="flex items-start gap-3 rounded-lg bg-lightPurple-lightest p-4">
          <StatusDot state="up_to_date" class="mt-1" />
          <div class="flex-1 text-sm">
            <p class="font-bold">
              Configuration terminée
            </p>
            <!-- This post when it is the gateway — or has just proven its
                 code, before the map says so — the known gateway otherwise;
                 while none has answered the search, the search, then the
                 post to look at. -->
            <p v-if="cpsStore.isLocalGateway || verified" class="text-gray-600">
              Le poste <span class="font-bold" :title="hostname ?? undefined">{{ shortHostname }}</span> est le
              poste passerelle de la pharmacie<template v-if="holder"> — carte de {{ holder }}</template>.
              Les vérifications ADAC de la pharmacie passent par lui.
            </p>
            <p v-else-if="remoteGateway" class="text-gray-600">
              Le poste <span class="font-bold">{{ remoteGateway.hostname }}</span> est le poste passerelle
              de la pharmacie<template v-if="remoteGateway.holder"> — carte de {{ remoteGateway.holder }}</template>.
              Les vérifications ADAC de ce poste passent par lui.
            </p>
            <p v-else-if="cpsStore.scanning" class="text-gray-600">
              Recherche du poste passerelle de la pharmacie…
            </p>
            <p v-else class="text-gray-600">
              Aucun poste passerelle ne répond pour le moment. Vérifiez que Connect est
              ouvert sur le poste qui tient la carte CPS de la pharmacie.
            </p>
          </div>
          <div class="flex items-center shrink-0 self-center ml-8">
            <BaseButton @click="restart">
              Recommencer la configuration
            </BaseButton>
          </div>
        </div>
      </template>
    </BaseCardBody>
  </BaseCard>
</template>
